import { updateChatItem } from '../store/appSlice';
import { AppDispatch } from '../store/store';
import readNDJSONStream from 'ndjson-readablestream';
import { formatISODateTimesInText } from '../pages/chat/helpers';
import {
  caseCreatedText,
  caseRegistrationConfirmationText,
  forbiddenErrorText,
  genericCaseErrorText,
  validationSendCaseErrorText,
  genericErrorText,
  unauthorizedErrorText,
} from './constants';
import { HttpStatus } from './http';
import {
  ChatResponseCase,
  ChatResponseContentStream,
  ChatResponseDraft,
  ChatResponse,
  CaseCreationStatus,
} from './types';
import { ChatAnswer, ChatAnswerType } from '../store/types';

export const handleResponse =
  (dispatch: AppDispatch) =>
  async (response: Response, id: number, question: string): Promise<void> => {
    try {
      let answer = '';
      let isStreaming = true;

      for await (const untypedChatResponse of readNDJSONStream(response.body!)) {
        if (!isStreaming) {
          continue;
        }

        const chatResponse = untypedChatResponse as ChatResponse;

        if (hasDraftCase(chatResponse)) {
          handleDraftCaseResponse(dispatch, chatResponse, id, question, answer);
          isStreaming = false;
        } else if (
          hasFailedCaseCreationStatus(chatResponse) &&
          (hasValidationErrors(chatResponse) || hasUserMessagesErrors(chatResponse))
        ) {
          handleValidationResultAtCaseCreationStep(dispatch, chatResponse, id, question);
          isStreaming = false;
        } else if (hasCaseCreationResult(chatResponse)) {
          handleCaseCreationResult(dispatch, chatResponse, response.status, id, question, answer);
          isStreaming = false;
        } else if (hasRegularResponse(chatResponse)) {
          answer = await handleRegularResponse(dispatch, chatResponse, id, question, answer);
        } else if (hasErrorResponse(chatResponse)) {
          handleErrorResponse(dispatch, id, question);
          isStreaming = false;
        }
      }

      if (isStreaming) {
        const newChatAnswer: ChatAnswer =
          !!answer && response.status === HttpStatus.OK
            ? {
                content: answer,
                type: ChatAnswerType.ok,
              }
            : {
                content: genericErrorText,
                type: ChatAnswerType.error,
              };

        dispatch(
          updateChatItem({
            id,
            question,
            answer: newChatAnswer,
          })
        );
      }
    } catch {
      dispatch(
        updateChatItem({
          id,
          question,
          answer: { content: genericErrorText, type: ChatAnswerType.error },
        })
      );
    }
  };

const hasFailedCaseCreationStatus = (chatResponse: ChatResponseCase) =>
  chatResponse.caseCreationStatus === CaseCreationStatus.failed;
const hasValidationErrors = (chatResponse: ChatResponseCase) =>
  chatResponse.validationErrors && chatResponse.validationErrors.length > 0;
const hasUserMessagesErrors = (chatResponse: ChatResponseCase) =>
  chatResponse.userErrorMessages && chatResponse.userErrorMessages.length > 0;
const hasRegularResponse = (chatResponse: ChatResponse) => !!chatResponse?.content;

const formatMarkdownContent = (content: string): string => {
  return content
    .replace(/^ +/gm, (match) => match.replace(/ /g, '&nbsp;')) // Replace leading spaces with &nbsp;
    .replace(/</g, '&lt;') // Replace all < with &lt;
    .replace(/>/g, '&gt;') // Replace all > with &gt;
    .replace(/&lt;(\/?em|\/?strong|\/?pre|\/?code)&gt;/g, '<$1>') // Restore allowed tags
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>') // Replace ** with <strong>
    .replace(/\*([^*\n]+)\*/g, '<em>$1</em>') // Replace * with <em>
    .replace(/`([^`\n]+)`/g, '<code>$1</code>') // Replace ` with <code>
    .replace(/```.*\n([\s\S]*?)\n```.*\n/g, '<pre><code>$1</code></pre>'); // replace code blocks
};

const handleRegularResponse = async (
  dispatch: AppDispatch,
  chatResponse: ChatResponseContentStream,
  id: number,
  question: string,
  answer: string
) => {
  const content = formatMarkdownContent(answer + chatResponse.content);

  const newChatAnswer: ChatAnswer = {
    content,
    type: ChatAnswerType.streaming,
  };

  await new Promise<void>((resolve) => {
    setTimeout(() => {
      dispatch(updateChatItem({ id, question, answer: newChatAnswer }));
      resolve();
    }, 20);
  });

  return content;
};

const hasCaseCreationResult = (chatResponse: ChatResponse) => !!chatResponse?.caseCreationStatus;

const httpResults = {
  [HttpStatus.Unauthorized as number]: {
    content: unauthorizedErrorText,
    type: ChatAnswerType.unauthorized,
  },
  [HttpStatus.Forbidden as number]: {
    content: forbiddenErrorText,
    type: ChatAnswerType.forbidden,
  },
};

const handleValidationResultAtCaseCreationStep = (
  dispatch: AppDispatch,
  chatResponse: ChatResponse,
  id: number,
  question: string
) => {
  let validationMessage = validationSendCaseErrorText;
  if (hasValidationErrors(chatResponse)) {
    const separator = chatResponse.validationErrors?.length == 1 ? '\n' : '\n - ';
    chatResponse?.validationErrors?.forEach((error) => {
      validationMessage += separator + normalizeValidationErrorMessage(error);
    });
  }
  if (hasUserMessagesErrors(chatResponse)) {
    const separator = chatResponse.userErrorMessages?.length == 1 ? '\n' : '\n - ';

    chatResponse?.userErrorMessages?.forEach((error) => {
      validationMessage += separator + normalizeValidationErrorMessage(error);
    });
  }

  const newChatAnswer: ChatAnswer = {
    content: validationMessage,
    caseId: chatResponse.caseId,
    type: ChatAnswerType.error,
    hasValidationErrors: true,
  };

  dispatch(updateChatItem({ id, question, answer: newChatAnswer }));
};

const normalizeValidationErrorMessage = (message: string) => {
  const idx = message.indexOf(':');
  if (idx < 0) {
    return message;
  }

  const left = message.substring(0, idx).trim();
  const right = message.substring(idx + 1).trim();

  if (!right || !left || !right.toLowerCase().includes(left.toLowerCase())) {
    return message.trim();
  }

  return right;
};

const handleCaseCreationResult = (
  dispatch: AppDispatch,
  chatResponse: ChatResponse,
  responseStatus: number,
  id: number,
  question: string,
  answer: string
) => {
  // Use the localized content from the backend if available, otherwise fall back to client-side generation
  const rawContent =
    chatResponse.caseCreationStatus === CaseCreationStatus.created
      ? chatResponse.content || caseCreatedText(chatResponse.caseId as number)
      : httpResults[responseStatus]?.content || genericCaseErrorText;

  const newType: ChatAnswerType =
    chatResponse.caseCreationStatus === CaseCreationStatus.created
      ? ChatAnswerType.caseCreated
      : httpResults[responseStatus]?.type || ChatAnswerType.error;

  const newChatAnswer: ChatAnswer = {
    content: formatMarkdownContent(answer + rawContent),
    caseId: chatResponse.caseId,
    caseLinkText: chatResponse.caseLinkText,
    type: newType,
  };

  dispatch(updateChatItem({ id, question, answer: newChatAnswer }));
};

const hasDraftCase = (chatResponse: ChatResponse) => !!chatResponse?.case;

const handleDraftCaseResponse = (
  dispatch: AppDispatch,
  chatResponse: ChatResponseDraft,
  id: number,
  question: string,
  answer: string
) => {
  // Use the localized content from the backend if available, otherwise fall back to client-side generation
  const rawContent = chatResponse.content || caseRegistrationConfirmationText(chatResponse.case);
  const newContent = formatMarkdownContent(formatISODateTimesInText(answer + rawContent));

  const newChatAnswer: ChatAnswer = {
    content: newContent,
    draft: chatResponse.case,
    type: ChatAnswerType.ok,
  };

  dispatch(updateChatItem({ id, question, answer: newChatAnswer }));
};

const hasErrorResponse = (chatResponse: ChatResponse) =>
  chatResponse.error || chatResponse.errors || typeof chatResponse === 'string';

const handleErrorResponse = async (dispatch: AppDispatch, id: number, question: string) => {
  const newChatAnswer: ChatAnswer = {
    content: genericErrorText,
    type: ChatAnswerType.error,
  };

  dispatch(updateChatItem({ id, question, answer: newChatAnswer }));
};
